import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  effect,
  inject,
  input,
  OnDestroy,
  OnInit,
  output,
  signal,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import {UntypedFormBuilder} from '@angular/forms';
import {SearchWidgetManagerService} from '../../../../../services/search-widget/search-widget-manager.service';
import {GtmService} from '../../../../../services/gtm.service';
import {SearchService} from '../../../../../services/search/search.service';
import {GtmEventEnum} from '../../../../../enums/GoogleEnums';
import {EventActionEnum, EventCategoryEnum} from '../../../../../enums/GaTrackingEnums';
import {Subscription} from 'rxjs';
import {SearchWidgetState} from '../../../typings/search-widget-state';
import {DLPageTypeEnum} from '../../../../../enums/DataLayerEnums';
import {debounceTime, distinctUntilChanged, pairwise, startWith} from 'rxjs/operators';
import {ToolsService} from '../../../../../services/tools.service';

@Component({
  selector: 'app-search-widget-v4-mobile-free-search-form',
  templateUrl: './search-widget-v4-mobile-free-search-form.component.html',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SearchWidgetV4MobileFreeSearchFormComponent implements OnInit, OnDestroy, AfterViewInit {
  public mainCareProviderTypeId = input<number>();
  public inferredCareProviderTypeAreaId = input<number>();
  public inferredCategoryTypeId = input<number>();
  public pageType = input<DLPageTypeEnum>();

  fieldFocused = output<boolean>();
  updateSuggestions = output<string>();

  public isTermFreeSearchNotEmpty = signal<boolean>(false);
  public viewIsInitialized = signal<boolean>(false);
  @ViewChild('freeSearchInput') protected freeSearchInput;
  protected freeSearchForm;
  private subscriptions: Subscription[] = [];
  private searchService = inject(SearchService);
  public state = this.searchService.searchWidgetState;
  private searchState = this.searchService.searchState;

  constructor(
    private formBuilder: UntypedFormBuilder,
    private searchWidgetManagerService: SearchWidgetManagerService,
    private gtmService: GtmService,
    private tools: ToolsService,
  ) {
    this.buildFreeSearchForm();

    effect(
      () => {
        this.updateFreeSearchFieldValues(this.state());
        this.setFieldFocus();
      },
      {allowSignalWrites: true},
    );
  }

  public ngOnInit(): void {
    this.setListeners();
  }

  public ngOnDestroy() {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  public ngAfterViewInit() {
    this.viewIsInitialized.set(true);
    this.setFieldFocus();
  }

  protected onFreeSearchFocus($event) {
    this.fieldFocused.emit(true);

    this.updateSuggestions.emit(this.freeSearchForm.controls['term'].value);

    const gtmTag = {
      event: GtmEventEnum.DEFAULT,
      Category: EventCategoryEnum.SEARCHNAV,
      Action: EventActionEnum.FREE_SEARCH_FIELD_CLICK,
      Label: 'click',
      Value: 1,
    };
    this.gtmService.pushTag(gtmTag);
    $event.stopPropagation();
  }

  protected onFreeSearchEnterHit(): void {
    if (this.freeSearchForm.controls['term'].value !== '') {
      this.searchWidgetManagerService.registerGtmGa4Event(
        `${EventCategoryEnum.SEARCHNAV} - free search enter hit`,
        GtmEventEnum.CTA_CLICK,
        this.pageType(),
        this.mainCareProviderTypeId(),
        this.inferredCareProviderTypeAreaId(),
        this.inferredCategoryTypeId(),
      );

      this.searchWidgetManagerService.freeSearchEnterHit(this.freeSearchForm.controls['term'].value, '');
      this.freeSearchInput.nativeElement.blur();
    }
  }

  protected onFreeSearchClear(): void {
    this.freeSearchForm.reset(
      {
        term: '',
      },
      {emitEvent: false},
    );

    this.searchWidgetManagerService.clearFreeSearchTerm(true); //place is no longer merged with free search VS-6708
  }

  private setFieldFocus(): void {
    if (
      this.state().freeSearchFieldFocus === true &&
      this.viewIsInitialized() === true &&
      !this.tools.isNil(this.freeSearchInput)
    ) {
      this.freeSearchInput.nativeElement.focus();
    }
  }

  private buildFreeSearchForm() {
    this.freeSearchForm = this.formBuilder.group(
      {
        term: [''],
      },
      {emitEvent: false},
    );
  }

  private setListeners() {
    this.subscriptions.push(
      this.freeSearchForm.valueChanges
        .pipe(debounceTime(500), distinctUntilChanged(), startWith(''), pairwise())
        .subscribe(([previous, current]) => {
          const previousTerm = previous.term;
          const currentTerm = current.term;

          if (previousTerm !== currentTerm) {
            this.updateSuggestions.emit(currentTerm);
            this.onFreeSearchValueChanged(currentTerm);
          }
        }),
    );
  }

  private updateFreeSearchFieldValues(state: SearchWidgetState): void {
    if (this.freeSearchForm.controls['term'].value !== state.autocompleteCurrentValue) {
      this.freeSearchForm.setValue(
        {
          term: state.autocompleteCurrentValue || '',
        },
        {emitEvent: false},
      );
    }
    this.isTermFreeSearchNotEmpty.set(this.freeSearchForm.controls['term'].value !== '');
    this.updateSuggestions.emit(state.autocompleteCurrentValue);
  }

  private onFreeSearchValueChanged(term: string): void {
    if (this.state().autocompleteCurrentValue !== term) {
      const newState = this.tools.clone(this.state());
      newState.autocompleteValueChanged = true;
      newState.autocompleteCurrentValue = term;
      newState.isLocationSearchVisible = term !== '';

      // const newSearchState = this.tools.clone(this.searchState());
      // newSearchState.holdResultsReload = true;

      this.isTermFreeSearchNotEmpty.set(term !== '');
      this.searchService.updateSearchWidgetState(newState);
      // this.searchService.updateSearchState(newSearchState);
    }
  }
}
